import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { CheckCircle, XCircle, List as ListIcon } from 'lucide-react';
import { Eye, FileText, Clock, Upload, PenTool, LayoutGrid } from '../components/icons';
import { actasAPI, activosAPI, consumiblesAPI } from '../services/api';
import { buildActaHistoricaUrl } from '../config';
import StatusBadge from '../components/StatusBadge';
import Tooltip from '../components/Tooltip';
import SignatureDrawer from '../components/SignatureDrawer';

const Actas = () => {

  const navigate = useNavigate();
  const { showToast } = useToast();
  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [misActivos, setMisActivos] = useState([]);
  const [misConsumibles, setMisConsumibles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [selectedAsignacion, setSelectedAsignacion] = useState(null);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [activeTab, setActiveTab] = useState('todas'); // 'todas', 'pendientes', 'firmadas', 'rechazadas'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('actasViewMode') || 'grid';
  });

  useEffect(() => {
    localStorage.setItem('actasViewMode', viewMode);
  }, [viewMode]);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showCreateSignaturePrompt, setShowCreateSignaturePrompt] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [actasResp, activosResp, consumiblesResp] = await Promise.all([
        actasAPI.getMisActas(),
        activosAPI.getMisActivos(),
        consumiblesAPI.getMisConsumibles()
      ]);
      setActas(actasResp.data);
      setMisActivos(activosResp.data || []);
      setMisConsumibles(consumiblesResp.data || []);
    } catch (error) {
      console.error('Error:', error);
      showToast('No se pudo cargar la información', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
    } else {
      showToast('Solo se permiten archivos PDF', 'error');
      e.target.value = null;
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedAsignacion) {
      showToast('Selecciona un archivo y una asignación', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('acta', selectedFile);
    if (selectedAsignacion) formData.append('asignacionId', selectedAsignacion.id);
    if (selectedMovimiento) formData.append('movimientoId', selectedMovimiento.id);

    try {
      await actasAPI.subirPdf(formData);
      showToast('Acta subida correctamente', 'success');
      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedAsignacion(null);
      setSelectedMovimiento(null);
      fetchData(); // Recargar lista
    } catch (error) {
      console.error('Error:', error);
      showToast('No se pudo subir el archivo', 'error');
    } finally {
      setUploading(false);
    }
  };

  // Funciones para firma digital
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    if (!selectedAsignacion && !selectedMovimiento) {
      showToast('No hay una asignación o insumo seleccionado', 'error');
      return;
    }

    setSigning(true);
    const payload = {
      asignacionId: selectedAsignacion?.id,
      movimientoId: selectedMovimiento?.id,
    };

    try {
      const response = await actasAPI.firmarDigital(payload);
      showToast(response.data.message || 'Firma aplicada correctamente', 'success');
      setShowSignatureModal(false);
      setSelectedAsignacion(null);
      setSelectedMovimiento(null);
      fetchData();
    } catch (error) {
      console.error('Error al firmar:', error);

      // Manejar el caso cuando el usuario no tiene firma
      if (error.response?.status === 400 && (error.response?.data?.options || typeof error.response?.data?.message === 'string')) {
        const options = error.response.data.options;

        // Mostrar mensaje con opciones
        const baseMsg = error.response.data.message || 'No tienes una firma digital configurada';
        const message = options
          ? `${baseMsg}\n\nOpciones:\n• ${options.createSignature}\n• ${options.uploadSignature}\n• ${options.uploadPdf}`
          : baseMsg;
        showToast(message, 'info');

        // Si hay opción de crear firma, mostrar el prompt dentro del modal
        if (options?.createSignature || (error.response?.data?.code === 'NO_SIGNATURE') || (baseMsg || '').toLowerCase().includes('firma digital')) {
          setShowCreateSignaturePrompt(true);
        }
      } else if (error.response?.status === 400 && error.response?.data?.message) {
        showToast(error.response.data.message, 'error');
      } else {
        showToast(error.response?.data?.message || 'No se pudo firmar el acta', 'error');
      }
    } finally {
      setSigning(false);
    }
  };

  const handleFirmarDigital = async (asignacionId, movimientoId) => {
    setSelectedAsignacion(asignacionId ? { id: asignacionId } : null);
    setSelectedMovimiento(movimientoId ? { id: movimientoId } : null);
    setShowCreateSignaturePrompt(false);
    setShowSignatureModal(true);
  };

  const handleCreateSignature = async (signatureFile) => {
    try {
      const formData = new FormData();
      formData.append('firma', signatureFile);

      await actasAPI.subirFirma(formData);
      showToast('Firma creada y guardada exitosamente', 'success');
      setShowSignatureDrawer(false);

      // Si el modal de firma estaba abierto, intentar firmar automáticamente
      if (showSignatureModal && selectedAsignacion) {
        setTimeout(() => {
          saveSignature();
        }, 1000);
      }
    } catch (error) {
      console.error('Error al crear firma:', error);
      showToast('Error al crear la firma', 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);



  // 🎯 FLUJO COMPLETO - 6 CONDICIONES DEL OJO AZUL
  const handlePrevisualizarActa = async (acta, asignacionId, movimientoId) => {
    try {
      let response;

      if (acta && acta.esHistorica && acta.rutaArchivo) {
        window.open(buildActaHistoricaUrl(acta.rutaArchivo), '_blank');
        return;
      }

      // 1. 🆕 PENDIENTE DE FIRMA - !acta (no existe acta)
      if (!acta) {
        if (asignacionId) {
          response = await actasAPI.previsualizarActa(asignacionId);
        } else if (movimientoId) {
          response = await actasAPI.previsualizarMovimiento(movimientoId);
        }
        showToast('Generando acta con firma de admin/soporte para que puedas previsualizar y firmar a mano', 'info');
      }
      // 2. ⏳ PENDIENTE DE APROBACIÓN
      else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'pdf_subido') {
        response = await actasAPI.descargarActa(acta.id);
        showToast('Previsualizando PDF que subiste para aprobación', 'info');
      }
      // 3. ✅ APROBADA
      else if (acta.estado?.toLowerCase() === 'aprobada' && acta.metodoFirma?.toLowerCase() === 'pdf_subido') {
        response = await actasAPI.descargarActa(acta.id);
        showToast('Previsualizando tu PDF aprobado', 'info');
      }
      // 4. ✍️ FIRMADA DIGITALMENTE
      else if (acta.estado?.toLowerCase() === 'firmada' && acta.metodoFirma?.toLowerCase() === 'digital') {
        response = await actasAPI.previsualizarActaFirmado(acta.id);
        showToast('Previsualizando acta con tu firma digital y firma de admin/soporte', 'info');
      }
      // 5. 📤 SUBIDA POR ADMIN/SOPORTE
      else if (acta.metodoFirma?.toLowerCase() === 'admin_subida') {
        response = await actasAPI.descargarActa(acta.id);
        showToast('Previsualizando PDF que subió el admin/soporte', 'info');
      }
      // 6. ❌ RECHAZADA
      else if (acta.estado?.toLowerCase() === 'rechazada' && acta.metodoFirma?.toLowerCase() === 'pdf_subido') {
        if (asignacionId) {
          response = await actasAPI.previsualizarActa(asignacionId);
        } else if (movimientoId) {
          response = await actasAPI.previsualizarMovimiento(movimientoId);
        }
        showToast('Generando acta con firma de admin/soporte para que puedas previsualizar y firmar a mano nuevamente', 'info');
      }
      // Caso por defecto
      else {
        if (asignacionId) {
          response = await actasAPI.previsualizarActa(asignacionId);
        } else if (movimientoId) {
          response = await actasAPI.previsualizarMovimiento(movimientoId);
        }
        showToast('Previsualizando acta', 'info');
      }

      if (response) {
        const url = window.URL.createObjectURL(response.data);
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error:', error);
      showToast('No se pudo previsualizar el acta', 'error');
    }
  };



  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pendiente de aprobación':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'firmada':
      case 'firmado':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'aprobada':
      case 'aprobado':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'rechazada':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getMetodoFirmaIcon = (metodo) => {
    switch (metodo?.toLowerCase()) {
      case 'digital':
        return <PenTool className="w-4 h-4" />;
      case 'pdf_subido':
        return <Upload className="w-4 h-4" />;
      case 'admin_subida':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getAsignacionesActivas = () => {
    const asignaciones = misActivos.map(a => ({
      id: a.asignacionId,
      tipo: 'asignacion',
      activo: a,
      display: `${a.codigo || 'S/N'} - ${a.nombreEquipo || a.nombre || 'Equipo'}`
    }));

    const movimientos = misConsumibles.map(m => ({
      id: m.id,
      tipo: 'movimiento',
      consumible: m.consumible,
      display: `${m.consumible?.nombre || 'Insumo'} (${m.cantidad})`
    }));

    return [...asignaciones, ...movimientos];
  };

  const handleRowClick = (acta) => {
    let targetId = null;
    if (acta.asignacionId) {
      targetId = acta.asignacionId;
    } else if (acta.movimientoId) {
      targetId = `mov_${acta.movimientoId}`;
    }

    if (!targetId) return;
    navigate(`/actas/${targetId}`);
  };

  const filteredActas = actas.filter(acta => {
    switch (activeTab) {
      case 'pendientes':
        return acta.estado?.toLowerCase() === 'pendiente' || acta.estado?.toLowerCase() === 'pendiente de aprobación' || acta.estado?.toLowerCase() === 'pendiente_de_aprobacion';
      case 'firmadas':
        return acta.estado?.toLowerCase() === 'firmada' || acta.estado?.toLowerCase() === 'firmado';
      case 'rechazadas':
        return acta.estado?.toLowerCase() === 'rechazada';
      default:
        return true; // todas
    }
  });



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mis Actas
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Gestiona tus actas de entrega y recepción
          </p>
        </div>


      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{actas.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pendientes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {actas.filter(a => a.estado?.toLowerCase() === 'pendiente' || a.estado?.toLowerCase() === 'pendiente de aprobación' || a.estado?.toLowerCase() === 'pendiente_de_aprobacion').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Firmadas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {actas.filter(a => a.estado?.toLowerCase() === 'firmada' || a.estado?.toLowerCase() === 'firmado').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rechazadas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {actas.filter(a => a.estado?.toLowerCase() === 'rechazada').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 flex justify-between items-end pb-2 overflow-x-auto scrollbar-hide">
        <nav className="-mb-2.5 flex space-x-8 min-w-max">
          <button
            onClick={() => setActiveTab('todas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'todas'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Todas ({actas.length})
          </button>
          <button
            onClick={() => setActiveTab('pendientes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'pendientes'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Pendientes ({actas.filter(a => a.estado?.toLowerCase() === 'pendiente' || a.estado?.toLowerCase() === 'pendiente de aprobación' || a.estado?.toLowerCase() === 'pendiente_de_aprobacion').length})
          </button>
          <button
            onClick={() => setActiveTab('firmadas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'firmadas'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Firmadas ({actas.filter(a => a.estado?.toLowerCase() === 'firmada' || a.estado?.toLowerCase() === 'firmado').length})
          </button>
          <button
            onClick={() => setActiveTab('rechazadas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'rechazadas'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            Rechazadas ({actas.filter(a => a.estado?.toLowerCase() === 'rechazada').length})
          </button>
        </nav>

        {/* Toggle Vista */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700 flex-shrink-0 ml-4 hidden sm:flex">
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            title="Vista de lista"
          >
            <ListIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            title="Vista de cuadrícula"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Lista de actas */}
      {filteredActas.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <FileText className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {activeTab === 'todas' ? 'No tienes actas' : `No hay actas ${activeTab}`}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            {activeTab === 'todas'
              ? 'Aún no se han generado actas para tus asignaciones o consumibles.'
              : activeTab === 'pendientes'
                ? '¡Genial! No tienes actas pendientes de firma actualmente.'
                : activeTab === 'firmadas'
                  ? 'Aquí aparecerán las actas que hayas firmado digitalmente o subido.'
                  : 'No se encontraron actas rechazadas en tu historial.'
            }
          </p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 xl:grid-cols-2 gap-6" : "flex flex-col gap-4"}>
          {filteredActas.map((acta) => (
            <div
              key={acta.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-all duration-300 group`}
            >
              <div className={`p-5 flex ${viewMode === 'list' ? 'flex-col sm:flex-row sm:items-center w-full gap-4' : 'flex-col h-full'}`}>
                <div className={`flex items-start justify-between ${viewMode === 'grid' ? 'mb-4' : 'min-w-[250px] w-full sm:w-auto'}`}>
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      {React.cloneElement(getMetodoFirmaIcon(acta.metodoFirma), { className: "w-7 h-7" })}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {acta.esHistorica && (
                          <span className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                            Histórica
                          </span>
                        )}
                        <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-1">
                          {acta.esHistorica ? (
                            acta.activo ? `${acta.activo.codigo} - ${acta.activo.categoria}` : 'Acta de Entrega Histórica'
                          ) : acta.asignacion?.activo ? (
                            `${acta.asignacion.activo.codigo} - ${acta.asignacion.activo.categoria}`
                          ) : (
                            `${acta.movimiento?.consumible?.nombre || 'Consumible'} - ${acta.movimiento?.consumible?.categoria || 'Entrega'}`
                          )}
                        </h3>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 space-x-3">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(acta.fechaCreacion).toLocaleDateString()}
                        </span>
                        <span>•</span>
                        <StatusBadge estado={acta.estado} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`${viewMode === 'list' ? 'flex-grow w-full sm:w-auto mt-4 sm:mt-0 sm:px-4 sm:border-x border-gray-100 dark:border-gray-700/50' : 'flex-grow'}`}>
                  <div className={`bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4 border border-gray-100 dark:border-gray-700/50 ${viewMode === 'grid' ? 'mb-4' : ''}`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Detalles del Acta</span>
                    </div>
                    <div className={`text-sm text-gray-600 dark:text-gray-400 ${viewMode === 'list' ? 'flex flex-row gap-4 flex-wrap' : 'space-y-1'}`}>
                      <p><span className="font-medium">Identificador:</span> #{acta.id.toString().padStart(5, '0')}</p>
                      <p><span className="font-medium">Método de Firma:</span> {acta.metodoFirma?.replace('_', ' ') || 'No especificado'}</p>
                    </div>
                    {acta.comentariosAprobacion && (
                      <div className="mt-3 border-t border-red-100 dark:border-red-900/30 pt-3">
                        <div className="flex items-start space-x-2 text-sm text-red-600 dark:text-red-400">
                          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <p><span className="font-medium">Motivo de rechazo:</span> {acta.comentariosAprobacion}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`flex justify-end space-x-2 ${viewMode === 'list' ? 'w-full sm:w-auto mt-4 sm:mt-0 flex-shrink-0' : 'pt-4 border-t border-gray-100 dark:border-gray-700'}`}>
                  {/* 👁️ OJO AZUL */}
                  <button
                    onClick={() => handlePrevisualizarActa(acta, acta.asignacion?.id || acta.asignacionId, acta.movimiento?.id || acta.movimientoId)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 font-medium transition-colors"
                    title="Ver Documento"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">Ver Acta</span>
                  </button>

                  {/* ✍️ PLUMA - Firmar digitalmente */}
                  {(!acta || acta.estado?.toLowerCase() === 'pendiente' || acta.estado?.toLowerCase() === 'rechazada') && (
                    <button
                      onClick={() => handleFirmarDigital(acta?.asignacion?.id || acta.asignacionId, acta?.movimiento?.id || acta.movimientoId)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 font-medium transition-colors"
                      title="Firmar Digitalmente"
                    >
                      <PenTool className="w-4 h-4" />
                      <span className="hidden sm:inline">Firmar</span>
                    </button>
                  )}

                  {/* 📤 UPLOAD - Subir PDF */}
                  {(!acta || acta.estado?.toLowerCase() === 'pendiente' || acta.estado?.toLowerCase() === 'rechazada') && (
                    <button
                      onClick={() => {
                        setSelectedAsignacion(acta?.asignacion || (acta.asignacionId ? { id: acta.asignacionId } : null));
                        setSelectedMovimiento(acta?.movimiento || (acta.movimientoId ? { id: acta.movimientoId } : null));
                        setShowUploadModal(true);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 font-medium transition-colors"
                      title="Subir Archivo Firmado Manualmente"
                    >
                      <Upload className="w-4 h-4" />
                      <span className="hidden sm:inline">Subir</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}


      {/* Modal de subida */}
      {showUploadModal && createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowUploadModal(false)}
        >
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Subir Acta Firmada
              </h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-5">
              {!selectedAsignacion && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Seleccionar Asignación/Insumo
                  </label>
                  <select
                    value={selectedAsignacion?.id || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setSelectedAsignacion(null);
                        setSelectedMovimiento(null);
                        return;
                      }
                      const found = getAsignacionesActivas().find(a => `${a.tipo}_${a.id}` === val);
                      if (found?.tipo === 'movimiento') {
                        setSelectedMovimiento({ id: found.id });
                        setSelectedAsignacion(null);
                      } else {
                        setSelectedAsignacion({ id: found.id });
                        setSelectedMovimiento(null);
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all dark:text-white"
                  >
                    <option value="">Selecciona una opción</option>
                    {getAsignacionesActivas().map(item => (
                      <option key={`${item.tipo}_${item.id}`} value={`${item.tipo}_${item.id}`}>
                        {item.display}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl p-6 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-all cursor-pointer relative group">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-500 mb-3 group-hover:scale-110 transition-transform">
                    <Upload className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {selectedFile ? selectedFile.name : "Haga clic para subir PDF"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Solo se permiten archivos PDF</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setSelectedAsignacion(null);
                    setSelectedMovimiento(null);
                  }}
                  className="flex-1 px-4 py-3 text-sm font-bold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || (!selectedAsignacion && getAsignacionesActivas().length > 0) || uploading}
                  className="flex-1 px-4 py-3 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/30"
                >
                  {uploading ? 'Subiendo...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de firma digital */}
      {showSignatureModal && createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowSignatureModal(false)}
        >
          <div className="relative mx-auto p-6 border w-full max-w-md shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-in fade-in zoom-in duration-200">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                <PenTool className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Firma Digital
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Confirma tu identidad para el acta seleccionada
              </p>
            </div>

            <div className="space-y-6">
              {showCreateSignaturePrompt ? (
                // Prompt para crear firma
                <div className="p-4 sm:p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-2xl">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-800/40 rounded-full flex items-center justify-center mb-3 text-yellow-600 dark:text-yellow-400">
                      <PenTool className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      Aún no tienes una firma configurada
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Para firmar digitalmente, por favor proporciona tu firma usando una de las siguientes opciones:
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                      <div className="relative group">
                        <input
                          type="file"
                          accept="image/png, image/jpeg"
                          onChange={(e) => {
                            if (e.target.files[0]) handleCreateSignature(e.target.files[0]);
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <div className="w-full flex flex-col items-center justify-center p-4 h-full bg-white dark:bg-gray-700 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl group-hover:border-blue-500 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-all">
                          <Upload className="w-6 h-6 text-gray-400 group-hover:text-blue-500 mb-2" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400">Subir imagen</span>
                          <span className="text-xs text-gray-500 mt-1">PNG o JPG</span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setShowSignatureModal(false);
                          setShowCreateSignaturePrompt(false);
                          setShowSignatureDrawer(true);
                        }}
                        className="w-full flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-700 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-xl hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all group"
                      >
                        <PenTool className="w-6 h-6 text-gray-400 group-hover:text-purple-500 mb-2" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400">Dibujar firma</span>
                        <span className="text-xs text-gray-500 mt-1">Usar mouse o táctil</span>
                      </button>
                    </div>

                    <button
                      onClick={() => setShowSignatureModal(false)}
                      className="mt-6 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      Cancelar y volver
                    </button>
                  </div>
                </div>
              ) : (
                // Información normal de firma digital
                <div className="space-y-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-5 text-center">
                    <CheckCircle className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200 leading-relaxed">
                      Se aplicará automáticamente tu <span className="font-bold underline decoration-blue-400">firma digital registrada</span> en este documento.
                    </p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        setShowSignatureModal(false);
                        setShowCreateSignaturePrompt(false);
                        setSelectedAsignacion(null);
                        setSelectedMovimiento(null);
                      }}
                      className="flex-1 py-3.5 text-sm font-bold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveSignature}
                      disabled={signing}
                      className="flex-1 py-3.5 text-sm font-bold text-white bg-green-600 shadow-lg shadow-green-500/30 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all"
                    >
                      {signing ? 'Firmando...' : 'Firmar Ahora'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )
      }

      {/* Componente para crear firma digital */}
      <SignatureDrawer
        isOpen={showSignatureDrawer}
        onClose={() => setShowSignatureDrawer(false)}
        onSave={handleCreateSignature}
        title="Crear Firma Digital"
      />
    </div >
  );
};

export default Actas;